import React, { useRef, useMemo, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Float } from '@react-three/drei';
import * as THREE from 'three';

const ParticleLogoInner = ({ groupPosition, shape = 'sphere' }) => {
    const pointsRef = useRef();
    const isMobile = window.innerWidth < 1024;
    const count = isMobile ? 6000 : 32000;

    // Create initial and target positions
    const [positions, targets, velocities, colors] = useMemo(() => {
        const pos = new Float32Array(count * 3);
        const target = new Float32Array(count * 3);
        const vel = new Float32Array(count * 3);
        const col = new Float32Array(count * 3);

        const radius = 3.2;
        const color1 = new THREE.Color("#2563eb"); // Blue
        const color2 = new THREE.Color("#7c3aed"); // Purple
        const color3 = new THREE.Color("#ef4444"); // Red
        const color4 = new THREE.Color("#3b82f6"); // Light Blue

        for (let i = 0; i < count; i++) {
            let tx, ty, tz;

            if (shape === 'box') {
                tx = (Math.random() - 0.5) * radius * 1.6;
                ty = (Math.random() - 0.5) * radius * 1.6;
                tz = (Math.random() - 0.5) * radius * 1.6;
            } else if (shape === 'torus') {
                const r = radius * 0.9;
                const t = radius * 0.35;
                const theta = Math.random() * Math.PI * 2;
                const phi = Math.random() * Math.PI * 2;
                tx = (r + t * Math.cos(phi)) * Math.cos(theta);
                ty = (r + t * Math.cos(phi)) * Math.sin(theta);
                tz = t * Math.sin(phi);
            } else if (shape === 'pyramid') {
                const base = radius * 1.6;
                const height = radius * 1.8;
                tx = (Math.random() - 0.5) * base;
                tz = (Math.random() - 0.5) * base;
                const progress = 1 - (Math.abs(tx) / (base / 2)) * (Math.abs(tz) / (base / 2));
                ty = (Math.random() - 0.5) * height * progress;
            } else { // default: sphere
                const theta = Math.random() * Math.PI * 2;
                const phi = Math.acos((Math.random() * 2) - 1);
                tx = radius * Math.sin(phi) * Math.cos(theta);
                ty = radius * Math.sin(phi) * Math.sin(theta);
                tz = radius * Math.cos(phi);
            }

            target[i * 3] = tx;
            target[i * 3 + 1] = ty;
            target[i * 3 + 2] = tz;

            // Start closer but still randomized
            pos[i * 3] = (Math.random() - 0.5) * 15;
            pos[i * 3 + 1] = (Math.random() - 0.5) * 12;
            pos[i * 3 + 2] = (Math.random() - 0.5) * 10;

            vel[i * 3] = 0;
            vel[i * 3 + 1] = 0;
            vel[i * 3 + 2] = 0;

            let mixedColor;
            const rand = Math.random();
            if (rand < 0.3) {
                mixedColor = color1.clone().lerp(color2, Math.random());
            } else if (rand < 0.6) {
                mixedColor = color2.clone().lerp(color3, Math.random());
            } else if (rand < 0.8) {
                mixedColor = color3.clone().lerp(color4, Math.random());
            } else {
                mixedColor = color1.clone().lerp(color4, Math.random());
            }
            col[i * 3] = mixedColor.r;
            col[i * 3 + 1] = mixedColor.g;
            col[i * 3 + 2] = mixedColor.b;
        }
        return [pos, target, vel, col];
    }, [count, shape]);

    useFrame((state) => {
        const time = state.clock.getElapsedTime();
        const positionsAttr = pointsRef.current.geometry.attributes.position;
        const array = positionsAttr.array;

        const formationDelay = 0.8;
        const formationT = Math.min(1, Math.max(0, (time - formationDelay) / 2.5));
        const attractionMultiplier = 0.0005 + (formationT * 0.0045);

        for (let i = 0; i < count; i++) {
            const i3 = i * 3;

            // 1. Precise Return Force (resilient attraction)
            velocities[i3] += (targets[i3] - array[i3]) * attractionMultiplier;
            velocities[i3 + 1] += (targets[i3 + 1] - array[i3 + 1]) * attractionMultiplier;
            velocities[i3 + 2] += (targets[i3 + 2] - array[i3 + 2]) * attractionMultiplier;

            // 2. Physical Damping
            velocities[i3] *= 0.91;
            velocities[i3 + 1] *= 0.91;
            velocities[i3 + 2] *= 0.91;

            array[i3] += velocities[i3];
            array[i3 + 1] += velocities[i3 + 1];
            array[i3 + 2] += velocities[i3 + 2];
        }
        positionsAttr.needsUpdate = true;
    });

    return (
        <points ref={pointsRef}>
            <bufferGeometry>
                <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
                <bufferAttribute attach="attributes-color" count={count} array={colors} itemSize={3} />
            </bufferGeometry>
            <pointsMaterial
                size={isMobile ? 0.08 : 0.06}
                vertexColors
                transparent
                opacity={0.7}
                sizeAttenuation
                blending={THREE.NormalBlending}
                depthWrite={false}
            />
        </points>
    );
};

const ParticleText = ({ groupPosition }) => {
    const pointsRef = useRef();
    const isMobile = window.innerWidth < 1024;
    const count = isMobile ? 3000 : 7000;

    const [positions, targets, velocities, colors] = useMemo(() => {
        const pos = new Float32Array(count * 3);
        const target = new Float32Array(count * 3);
        const vel = new Float32Array(count * 3);
        const col = new Float32Array(count * 3);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 1000;
        canvas.height = 250;
        ctx.fillStyle = 'white';
        ctx.font = 'bold 150px Inter, Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('MEHARETECH', 500, 125);

        const pixels = ctx.getImageData(0, 0, 1000, 250).data;
        const sampledPoints = [];
        for (let y = 0; y < 250; y += 2) {
            for (let x = 0; x < 1000; x += 2) {
                if (pixels[(y * 1000 + x) * 4 + 3] > 180) {
                    sampledPoints.push({ x: (x - 500) * 0.0125, y: -(y - 125) * 0.0125 });
                }
            }
        }

        for (let i = 0; i < count; i++) {
            const pt = sampledPoints[Math.floor(Math.random() * sampledPoints.length)] || { x: 0, y: 0 };
            target[i * 3] = pt.x;
            target[i * 3 + 1] = pt.y;
            target[i * 3 + 2] = 0;
            pos[i * 3] = (Math.random() - 0.5) * 10;
            pos[i * 3 + 1] = (Math.random() - 0.5) * 10;
            pos[i * 3 + 2] = (Math.random() - 0.5) * 5;

            let mixedColor;
            const rand = Math.random();
            if (rand < 0.3) mixedColor = new THREE.Color("#2563eb").lerp(new THREE.Color("#7c3aed"), Math.random());
            else if (rand < 0.6) mixedColor = new THREE.Color("#7c3aed").lerp(new THREE.Color("#ef4444"), Math.random());
            else mixedColor = new THREE.Color("#2563eb").lerp(new THREE.Color("#3b82f6"), Math.random());

            col[i * 3] = mixedColor.r;
            col[i * 3 + 1] = mixedColor.g;
            col[i * 3 + 2] = mixedColor.b;
        }
        return [pos, target, vel, col];
    }, [count]);

    useFrame((state) => {
        const time = state.clock.getElapsedTime();
        const positionsAttr = pointsRef.current.geometry.attributes.position;
        const array = positionsAttr.array;

        const formationT = Math.min(1, Math.max(0, (time - 1.0) / 2.0));
        const attractionMultiplier = 0.001 + (formationT * 0.025);

        for (let i = 0; i < count; i++) {
            const i3 = i * 3;
            // Enhanced Shape Preservation
            velocities[i3] += (targets[i3] - array[i3]) * attractionMultiplier;
            velocities[i3 + 1] += (targets[i3 + 1] - array[i3 + 1]) * attractionMultiplier;
            velocities[i3 + 2] += (targets[i3 + 2] - array[i3 + 2]) * attractionMultiplier;

            velocities[i3] *= 0.91;
            velocities[i3 + 1] *= 0.91;
            velocities[i3 + 2] *= 0.91;
            array[i3] += velocities[i3];
            array[i3 + 1] += velocities[i3 + 1];
            array[i3 + 2] += velocities[i3 + 2];
        }
        positionsAttr.needsUpdate = true;
    });

    return (
        <points ref={pointsRef}>
            <bufferGeometry>
                <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
                <bufferAttribute attach="attributes-color" count={count} array={colors} itemSize={3} />
            </bufferGeometry>
            <pointsMaterial
                size={0.12}
                vertexColors
                transparent
                opacity={0.8}
                sizeAttenuation
                blending={THREE.NormalBlending}
                depthWrite={false}
            />
        </points>
    );
};

const RotatingGroup = ({ children }) => {
    const ref = useRef();
    useFrame((state) => {
        if (ref.current) ref.current.rotation.y = state.clock.getElapsedTime() * 0.05;
    });
    return <group ref={ref}>{children}</group>;
};

const ParticleLogo = () => {
    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 1024);
    const [shape, setShape] = React.useState('sphere');

    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const groupPosition = isMobile ? [0, 0, 0] : [5.5, 0, 0];
    const cameraPosition = isMobile ? [0, 0, 22] : [0, 0, 15];

    if (isMobile) return null;

    return (
        <div className="absolute inset-0 w-full h-full pointer-events-none">
            <Canvas
                camera={{ position: cameraPosition, fov: 45 }}
                dpr={[1, 2]}
                gl={{ antialias: true, alpha: true, powerPreference: "high-performance", stencil: false, depth: true }}
                className="pointer-events-none"
            >
                <ambientLight intensity={1} />
                <pointLight position={[10, 10, 10]} intensity={1.5} />
                <group position={groupPosition}>
                    <RotatingGroup>
                        <ParticleLogoInner groupPosition={groupPosition} shape={shape} />
                    </RotatingGroup>
                    <Suspense fallback={null}>
                        <ParticleText groupPosition={groupPosition} />
                    </Suspense>
                </group>
            </Canvas>
        </div>
    );
};

export default ParticleLogo;
